// eslint-disable-next-line wix-editor/no-internal-import
import {CONTAINER_TYPE, DATA} from '@wix/component-protocol/schema'
import type {
    DefinitionObject,
    RefInfo,
    Builder,
    BuilderTypes,
    Experiment,
    BuilderStyle
} from '@wix/document-services-types'
import {chain, mapValues, pickBy, isEmpty} from 'lodash'
import {getDataTypeSchema, type Schema, getSlotsSchema, generateStyleSchema} from './builderTypesToSchemas'
import {DATA_TYPES} from '../../constants/constants'

export type {Schema} from './builderTypesToSchemas'

export interface BuilderCompDetails {
    definition: Partial<DefinitionObject> & {builderTypes: BuilderTypes}
    schemas: Record<string, Record<string, Schema>>
    identifier: string
    refs: Record<string, Record<string, RefInfo[]>>
}

export interface DeriveFromManifestConfig {
    isContainer: boolean
    hasDynamicContainers: boolean
}

export function deriveFromManifest(
    identifier: string,
    manifest: Builder.ResolvedManifest,
    experimentInstance?: Experiment,
    config: DeriveFromManifestConfig = {isContainer: false, hasDynamicContainers: false}
): BuilderCompDetails[] {
    if (experimentInstance?.isOpen('dm_schemaResourceLoader')) {
        return []
    }
    const innerElementsMapType = `${identifier}.InnerElementsMap`
    const styleType = `${identifier}.Style` satisfies BuilderStyle['type']
    const dataType = `${identifier}.Data`
    const innerElementsMetaDataType = `${identifier}.InnerElementsMetaData`
    const presetsType = `${identifier}.Presets`
    const slotsType = `${identifier}.Slots`
    const innerElements = manifest.editorElement?.elements ?? {}
    const isDraftSchema = !!experimentInstance?.isOpen('dm_draftBuilderSchemas')
    const dataSchema = generateDataSchema(manifest.editorElement?.data ?? {}, isDraftSchema, config)
    const root: BuilderCompDetails = {
        identifier,
        definition: {
            type: getType(manifest.editorElement?.data ?? {}),
            dataTypes: [dataType, ''],
            builderTypes: {
                innerElementsMap: innerElementsMapType,
                style: styleType,
                innerElementTypes: mapValues(innerElements, (_element, name: string) => `${identifier}|${name}`),
                data: dataType,
                presets: presetsType,
                slots: slotsType,
                innerElementsMetaData: innerElementsMetaDataType,
                referredType: identifier
            }
        },
        schemas: {
            innerElements: {
                [innerElementsMapType]: generateInnerElementsMapSchema(identifier, manifest, isDraftSchema)
            },
            style: {
                [styleType]: generateStyleSchema(manifest.editorElement ?? {}, isDraftSchema)
            },
            data: {
                [dataType]: dataSchema
            },
            innerElementsMetaData: {
                [innerElementsMetaDataType]: generateInnerElementsMetaDataSchema(innerElements, isDraftSchema)
            },
            slots: {
                [slotsType]: generateSlotsSchema(manifest.editorElement?.data ?? {}, isDraftSchema, config)
            },
            multilingualTranslations: {
                [dataType]: dataSchema
            },
            presets: {
                [presetsType]: generatePresetsSchema(manifest.editorElement?.presets ?? {}, isDraftSchema)
            }
        },
        refs: {
            innerElements: {
                [innerElementsMapType]: Object.keys(manifest.editorElement?.elements ?? {}).map(key => ({
                    jsonPointer: `${DATA_TYPES.innerElements}/${key}`,
                    isList: false,
                    referencedMap: 'innerElements',
                    isRefOwner: true,
                    shouldCollect: true,
                    shouldValidate: true,
                    refTypes: ['InnerElement'],
                    path: [DATA_TYPES.innerElements, key]
                }))
            }
        }
    }

    const innerElementPairs = Object.entries(innerElements)

    const children = innerElementPairs.flatMap(([name, element]) =>
        deriveFromManifest(`${identifier}|${name}`, {editorElement: element.inlineElement}, experimentInstance, config)
    )

    if (root.definition.type === 'RepeaterContainer') {
        root.definition.type = 'RepeaterContainer'
    } else if (config.isContainer || config.hasDynamicContainers || root.definition.type === 'Container') {
        root.definition.type = 'Container'
    } else {
        root.definition.type = 'Component'
    }

    return [root, ...children]
}

function generateSchema(
    name: (typeof DATA_TYPES)['theme' | 'data' | 'slots'],
    props: Record<string, Schema>,
    isDraftSchema: boolean,
    additionalProperties?: Record<string, Schema> | false | {$ref: string}
) {
    return {
        type: ['object', 'null'],
        additionalProperties: false,
        isTopLevelDataNode: true,
        properties: {
            ...topLevelDataNodeProperties(),
            [name]: {
                type: 'object',
                additionalProperties: additionalProperties || false,
                properties: props,
                isDraftSchema
            }
        },
        isDraftSchema
    }
}

function generateSlotsSchema(data: Builder.Data, isDraftSchema: boolean, config: DeriveFromManifestConfig) {
    const containerData = pickBy(data, dataItem => dataItem.dataType === DATA.DATA_TYPE.container)

    const slotAdditionalProperties = config.hasDynamicContainers
        ? {
              $ref: 'SlotRef'
          }
        : false
    if (!isEmpty(containerData)) {
        // If we have a container, we need to set the isContainer flag to true as side effect (within inner elements as well)
        config.isContainer = true
        const containerSchema = generateSchema(
            DATA_TYPES.slots,
            mapValues(containerData, dataItem => {
                return getSlotsSchema(dataItem.dataType)
            }),
            isDraftSchema,
            slotAdditionalProperties
        )

        return containerSchema
    }
    const result = config.hasDynamicContainers
        ? generateSchema(DATA_TYPES.slots, {}, isDraftSchema, slotAdditionalProperties)
        : {}
    return result
}

function generateDataSchema(
    data: Builder.Data,
    isDraftSchema: boolean,
    config: DeriveFromManifestConfig
): ReturnType<typeof generateSchema> {
    const filteredData = pickBy(data, dataItem => dataItem.dataType !== DATA.DATA_TYPE.container)
    return generateSchema(
        DATA_TYPES.data,
        mapValues(filteredData, dataItem => {
            const extraValidationParameters = dataItem.dataType
                ? dataItem[dataItem.dataType as keyof Builder.DataItem]
                : {}
            return getDataTypeSchema(dataItem.dataType, extraValidationParameters, config)
        }),
        isDraftSchema
    )
}

function generateInnerElementsMetaDataSchema(
    innerElements: Builder.ResolvedEditorElement['elements'],
    isDraftSchema: boolean
): Record<string, Schema> {
    return {
        type: ['object', 'null'],
        additionalProperties: false,
        isTopLevelDataNode: true,
        properties: {
            ...topLevelDataNodeProperties(),
            elementsRemovalState: {
                type: 'object',
                properties: chain(innerElements)
                    .pickBy(element => element.inlineElement?.behaviors?.removable)
                    .mapValues(() => ({enum: ['REMOVED']}))
                    .value()
            }
        },
        isDraftSchema
    }
}

function generatePresetsSchema(presets: Builder.Presets, isDraftSchema: boolean): Record<string, Schema> {
    const presetEnum = Object.keys(presets)
    const properties: Record<string, any> = {
        ...topLevelDataNodeProperties()
    }
    if (presetEnum.length > 0) {
        properties.presetName = {
            type: 'string',
            enum: presetEnum,
            isDraftSchema
        }
    }
    return {
        type: ['object'],
        additionalProperties: false,
        isTopLevelDataNode: true,
        properties,
        isDraftSchema
    }
}

function generateInnerElementsMapSchema(
    identifier: string,
    manifest: Builder.ResolvedManifest,
    isDraftSchema: boolean
) {
    const ownProperties = mapValues(manifest.editorElement?.elements, () => ({
        type: 'string',
        pseudoType: ['ref'],
        refTypes: ['InnerElement'],
        referencedMap: 'innerElements',
        isRefOwner: true,
        shouldCollect: true,
        shouldValidate: true
    }))
    return {
        type: 'object',
        additionalProperties: false,
        isTopLevelDataNode: true,
        properties: {
            ...topLevelDataNodeProperties(),
            [DATA_TYPES.innerElements]: {
                type: 'object',
                properties: {
                    ...ownProperties
                }
            },
            isDraftSchema
        },
        isDraftSchema
    }
}

function getType(data: Builder.Data) {
    const containerData = pickBy(data, dataItem => dataItem.dataType === DATA.DATA_TYPE.container)
    const templateContainerData = pickBy(
        containerData,
        dataItem => dataItem.container?.containerType === CONTAINER_TYPE.template
    )
    if (!isEmpty(templateContainerData)) {
        return 'RepeaterContainer'
    }
    if (!isEmpty(containerData)) {
        return 'Container'
    }
    return 'Component'
}

export function topLevelStyleNodeProperties(): Record<string, Schema> {
    return {
        spx: {
            $ref: 'SPXReferenceDefinition'
        }
    }
}

export function topLevelDataNodeProperties(): Record<string, Schema> {
    return {
        type: {
            type: 'string'
        },
        id: {
            type: 'string'
        },
        metaData: {
            type: 'object',
            properties: {
                pageId: {
                    type: 'string'
                },
                isPreset: {
                    type: 'boolean'
                },
                schemaVersion: {
                    type: 'string'
                },
                isHidden: {
                    type: 'boolean'
                },
                logicalName: {
                    type: 'string'
                },
                description: {
                    type: 'string',
                    maxLength: 4000
                },
                sig: {
                    type: 'string'
                }
            }
        }
    }
}
